import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, type To } from "react-router";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { adminApi, ApiError, apiErrorMessage } from "@/lib/api";
import { showAdminToast } from "@/lib/admin-toast";
import {
  queryKeys,
  useProductKeysQuery,
} from "@/lib/queries";
import { useTimezone } from "@/lib/timezone";
import type { LicensePatchRequest, ProductKeyMetadata, SearchResult } from "@/lib/types";
import { parseJsonObject } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BusyLabel, InlineAlert } from "@/components/admin/feedback";
import { LicenseConfigurationFields } from "@/components/admin/license-configuration-fields";
import { configurationDraftIsValid } from "@/components/admin/configuration-validation";
import { ConfigurationChangeSummary } from "@/components/admin/configuration-change-summary";
import {
  getValidityError,
  validityErrorMessage,
  type ValidityError,
} from "@/lib/license-validity";

export function EditLicenseFormContainer({
  result,
  allowed,
  suggestedMaxDevices,
  initialReason,
  savedAt,
  notifyOnSave = false,
  overviewHref,
  overviewState,
  onCancel,
  onSuccess,
  onDirtyChange,
}: {
  result: SearchResult;
  allowed: boolean;
  suggestedMaxDevices?: number;
  initialReason?: string;
  savedAt?: string;
  notifyOnSave?: boolean;
  overviewHref?: To;
  overviewState?: unknown;
  onCancel: () => void;
  onSuccess: () => void;
  /** Reports whether the form has unsaved edits; the edit page uses this to guard leaving. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation();
  const { timezoneAbbr, formatDateTime } = useTimezone();
  const saveStatusRef = useRef<HTMLParagraphElement>(null);
  const queryClient = useQueryClient();
  const [baseline, setBaseline] = useState(result);
  const [status, setStatus] = useState(result.license.status);
  const [alias, setAlias] = useState(result.license.alias ?? "");
  const [validFrom, setValidFrom] = useState(result.license.valid_from);
  const [validUntil, setValidUntil] = useState(result.license.valid_until ?? "");
  const [policy, setPolicy] = useState(
    policyJson(result, suggestedMaxDevices),
  );
  const [entitlements, setEntitlements] = useState(
    JSON.stringify(stripEntitlementKeys(result.license.entitlements ?? {}), null, 2),
  );
  const [reason, setReason] = useState(initialReason ?? "");
  const [configurationInvalid, setConfigurationInvalid] = useState(false);
  const [validation, setValidation] = useState<string | null>(null);
  const [reasonInvalid, setReasonInvalid] = useState(false);
	const [reductionOpen, setReductionOpen] = useState(false);
	const [reductionTarget, setReductionTarget] = useState<number | null>(null);
	const [selectedKeyIDs, setSelectedKeyIDs] = useState<string[]>([]);
	const productKeysQuery = useProductKeysQuery(result.license.id);
	const activeKeys = useMemo(() => productKeysQuery.data?.pages.flatMap((page) => page.product_keys).filter((key) => key.status === "active") ?? [], [productKeysQuery.data]);
  const [validFromInvalid, setValidFromInvalid] = useState(false);
  const [validUntilInvalid, setValidUntilInvalid] = useState(false);
  const [validityError, setValidityError] = useState<ValidityError | null>(null);
  const validityMessage = validityError && validityErrorMessage(t, validityError);
  const validFromLocked = result.activation_count > 0;
  const expiryRequirement = result.license.model === "time_limited"
    ? t("licenses.expiryRequired", "required for Time limited")
    : result.license.model === "perpetual"
      ? t("licenses.expiryForbidden", "not applicable for Perpetual")
      : t("licenses.expiryOptional", "optional for Node locked");
  const expiryPlaceholder = result.license.model === "time_limited"
    ? t("licenses.expiryPlaceholderRequired", "Set expiry (required)")
    : result.license.model === "perpetual"
      ? t("licenses.expiryPlaceholderForbidden", "No expiry for Perpetual")
      : t("licenses.expiryPlaceholderOptional", "Set expiry (optional)");

  // Unsaved-edit signal for the hosting page's leave guard. Baseline is the
  // loaded License plus any handoff prefill; changing any field marks dirty.
  const baselinePolicy = useMemo(() => policyJson(baseline, suggestedMaxDevices), [baseline, suggestedMaxDevices]);
  const dirty = configurationInvalid || status !== baseline.license.status
    || alias !== (baseline.license.alias ?? "")
    || validFrom !== baseline.license.valid_from
    || validUntil !== (baseline.license.valid_until ?? "")
    || entitlements !== JSON.stringify(stripEntitlementKeys(baseline.license.entitlements ?? {}), null, 2)
    || policy !== baselinePolicy
    || reason !== (initialReason ?? "");
  useEffect(() => {
    if (savedAt && !dirty) saveStatusRef.current?.focus({ preventScroll: true });
  }, [savedAt, dirty]);

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(() => {
    // Background seat updates must not replace an in-progress Configuration draft.
    if (dirty && baseline.license.id === result.license.id) return;
    setBaseline(result);
    setStatus(result.license.status);
    setAlias(result.license.alias ?? "");
    setValidFrom(result.license.valid_from);
    setValidUntil(result.license.valid_until ?? "");
    setPolicy(policyJson(result, suggestedMaxDevices));
    setEntitlements(
      JSON.stringify(stripEntitlementKeys(result.license.entitlements ?? {}), null, 2),
    );
    setValidFromInvalid(false);
    setValidUntilInvalid(false);
    setValidation(null);
    setValidityError(null);
    setReason(initialReason ?? "");
    setReasonInvalid(false);
  }, [baseline.license.id, dirty, initialReason, result, suggestedMaxDevices]);

	useEffect(() => {
		if (reductionOpen && productKeysQuery.hasNextPage && !productKeysQuery.isFetchingNextPage) {
			void productKeysQuery.fetchNextPage();
		}
	}, [reductionOpen, productKeysQuery.fetchNextPage, productKeysQuery.hasNextPage, productKeysQuery.isFetchingNextPage]);

  function clearValidityError() {
    setValidFromInvalid(false);
    setValidUntilInvalid(false);
    setValidityError(null);
  }

  function reportValidityError(nextValidityError: ValidityError) {
    const invalidFrom = nextValidityError === "valid_from_required";
    setValidFromInvalid(invalidFrom);
    setValidUntilInvalid(!invalidFrom);
    setValidityError(nextValidityError);
    requestAnimationFrame(() => document.getElementById(
      invalidFrom ? "license-edit-valid-from" : "license-edit-valid-until",
    )?.focus());
  }

  const mutation = useMutation({
    mutationFn: (body: LicensePatchRequest) =>
      adminApi.updateLicense(result.license.id, body),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin", "search"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "license-index"] }),
      ]);
      await queryClient.resetQueries({
        queryKey: queryKeys.timeline(result.license.id),
      });
      if (notifyOnSave) showAdminToast(t("licenses.configurationSaved", "Saved"));
      onSuccess();
    },
    onError: (error) => setValidation(
      error instanceof ApiError && error.code === "license_has_activations"
        ? t("licenses.licenseHasActivationsError", "Activation availability cannot change after Activation history exists. Refresh the License and try again.")
        : apiErrorMessage(error),
    ),
  });

	const reduceMutation = useMutation({
		mutationFn: ({ maxDevices, productKeyIDs }: { maxDevices: number; productKeyIDs: string[] }) =>
			adminApi.reduceSeatLimit(result.license.id, { max_devices: maxDevices, product_key_ids: productKeyIDs, reason: reason.trim() }),
		onSuccess: async () => {
			setReductionOpen(false);
			setReductionTarget(null);
			setSelectedKeyIDs([]);
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: ["admin", "search"] }),
				queryClient.resetQueries({ queryKey: queryKeys.productKeys(result.license.id) }),
				queryClient.resetQueries({ queryKey: queryKeys.timeline(result.license.id) }),
			]);
			if (notifyOnSave) showAdminToast(t("licenses.seatLimitReduced", "Seat limit reduced and selected seats revoked"));
			onSuccess();
		},
		onError: (error) => setValidation(apiErrorMessage(error)),
	});

  const saving = mutation.isPending || reduceMutation.isPending;
  const hasHandoffChange = suggestedMaxDevices !== undefined && suggestedMaxDevices > result.license.policy.max_devices;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!allowed || saving) return;
    if (!reason.trim()) {
      setReasonInvalid(true);
      setValidation(t("licenses.mutationReasonRequired", "A reason is required for every License change."));
      requestAnimationFrame(() => document.getElementById("license-edit-reason")?.focus());
      return;
    }
    const nextValidityError = getValidityError({
      model: result.license.model,
      validFrom,
      validUntil,
      requireValidFrom: true,
    });
    if (nextValidityError) {
      reportValidityError(nextValidityError);
      return;
    }
    let parsedEntitlements: Record<string, unknown>;
    if (!configurationDraftIsValid(event.currentTarget)) return;
    let parsedPolicy: Record<string, unknown>;
    try {
      parsedEntitlements = parseJsonObject(entitlements);
      parsedPolicy = parseJsonObject(policy);
    } catch (error) {
      return setValidation(
        error instanceof Error
          ? error.message
          : t("licenses.jsonValidationError", "Entitlements and policy must be valid JSON objects."),
      );
    }
    const count = parsedPolicy.max_devices == null
      ? result.license.policy.max_devices : parsedPolicy.max_devices;
    if (typeof count !== "number" || !Number.isInteger(count) || count < 1)
      return setValidation(t("licenses.maxDevicesValidationError", "Max devices must be a positive whole number."));

    const currentLimit = result.license.policy.max_devices;
		const activeKeyCount = productKeysQuery.data?.pages[0]?.active_key_count ?? activeKeys.length;
		if (count < currentLimit && count < activeKeyCount) {
			const otherPolicyChanges = Object.entries(parsedPolicy).some(([key, value]) => key !== "max_devices"
				&& JSON.stringify(value) !== JSON.stringify((result.license.policy as unknown as Record<string, unknown>)[key]));
			if (otherPolicyChanges || alias !== (result.license.alias ?? "") || status !== result.license.status || validFrom !== result.license.valid_from || validUntil !== (result.license.valid_until ?? "") || entitlements !== JSON.stringify(stripEntitlementKeys(result.license.entitlements ?? {}), null, 2)) {
				return setValidation(t("licenses.seatLimitSeparateChange", "Save other License changes first. Reducing the seat limit is a separate destructive action."));
			}
			setReductionTarget(count);
			setSelectedKeyIDs([]);
			setReductionOpen(true);
			return;
		}

		const body: LicensePatchRequest = {
      status,
      alias,
      ...(!validFromLocked ? { valid_from: validFrom } : {}),
      valid_until: validUntil || null,
      policy: parsedPolicy as LicensePatchRequest["policy"],
      entitlements: parsedEntitlements as LicensePatchRequest["entitlements"],
      reason: reason.trim(),
    };
    setValidation(null);
    setReasonInvalid(false);
    clearValidityError();
    mutation.mutate(body);
  }

  return (
    <>
    <form
      className="rounded-lg border border-line bg-panel p-5 sm:p-7"
      onSubmit={submit}
      noValidate
    >
      <fieldset disabled={!allowed || saving} className="min-w-0 space-y-6">
        <div className="flex items-center justify-between border-b border-line pb-4">
          <div>
            <h2 className="font-semibold text-ink">{t("search.details.licenseDetail", "License details")}</h2>
            <p className="mt-0.5 text-xs text-muted">
              {result.customer.name} · {result.product.name}
            </p>
          </div>
          <Badge variant="info">{t("licenses.auditedMutation", "Audited mutation")}</Badge>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="license-edit-alias">{t("licenses.alias", "Alias")}</Label>
            <Input
              id="license-edit-alias"
              value={alias}
              onChange={(event) => setAlias(event.target.value)}
              placeholder={t("licenses.aliasPlaceholder", "e.g. Design team 2026")}
              maxLength={80}
            />
            <p className="text-xs text-muted">{t("licenses.aliasHelp", "Shown in License lists instead of the License ID. Maximum 80 characters. Admin metadata only; never included in Signed License Tokens.")}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor={status === "revoked" ? undefined : "license-edit-status"}>{t("common.status", "Status")}</Label>
            {result.license.status === "revoked" || status === "revoked" ? (
              <p className="flex h-9 items-center text-sm text-muted">
                {t("common.revoked", "Revoked")}
              </p>
            ) : (
              <Select
                id="license-edit-status"
                value={status}
                onChange={(event) => setStatus(event.target.value as typeof status)}
              >
                <option value="active">{t("common.active", "Active")}</option>
                <option value="suspended">{t("common.suspended", "Suspended")}</option>
                <option value="expired">{t("common.expired", "Expired")}</option>
              </Select>
            )}
            <p className="text-xs text-muted">{t("licenses.revokeElsewhere", "Revoke this License with Revoke license on this page.")}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="license-edit-valid-from">
              {t("licenses.availableFrom", { tz: timezoneAbbr, defaultValue: `Available for activation from (${timezoneAbbr})` })}
            </Label>
            <DateTimePicker
              id="license-edit-valid-from"
              value={validFrom}
              onValueChange={(nextValue) => {
                setValidFrom(nextValue);
                if (nextValue && validityError === "valid_from_required") clearValidityError();
                if (validityError === "validity_window") clearValidityError();
              }}
              placeholder={t("licenses.availableFromPlaceholder", "Allow activation immediately")}
              disabled={validFromLocked || !allowed || saving}
              invalid={validFromInvalid}
              aria-describedby={validFromInvalid
                ? "license-edit-valid-from-help license-edit-valid-from-error"
                : "license-edit-valid-from-help"}
            />
            {validityError === "valid_from_required" && (
              <p id="license-edit-valid-from-error" className="text-xs text-danger">
                {validityErrorMessage(t, validityError)}
              </p>
            )}
            <p id="license-edit-valid-from-help" className="text-xs text-muted">
              <span className="block">
                {validFromLocked
                  ? t("licenses.validFromLocked", "Locked because this License has Activation history.")
                  : t("licenses.validFromEditable", "Can be changed until the first Activation.")}
              </span>
              {!validFromLocked && (
                <span className="mt-1 block">
                  {t("licenses.scheduledAvailabilityHelp", "Product Keys can be issued now; Activation is rejected until this time.")}
                </span>
              )}
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="license-edit-valid-until">
              {t("licenses.expiresAt", { tz: timezoneAbbr, defaultValue: `Expires at (${timezoneAbbr})` })}{" "}
              <span className="font-normal text-muted">({expiryRequirement})</span>
            </Label>
            <DateTimePicker
              disabled={!allowed || saving}
              id="license-edit-valid-until"
              value={validUntil}
              onValueChange={(nextValue) => {
                setValidUntil(nextValue);
                if (
                  (result.license.model !== "time_limited" || nextValue) &&
                  (result.license.model !== "perpetual" || !nextValue)
                ) {
                  clearValidityError();
                }
              }}
              placeholder={expiryPlaceholder}
              invalid={validUntilInvalid}
              aria-describedby={validUntilInvalid ? "license-edit-valid-until-error" : undefined}
            />
            {validityError && validityError !== "valid_from_required" && (
              <p id="license-edit-valid-until-error" className="text-xs text-danger">
                {validityErrorMessage(t, validityError)}
              </p>
            )}
            <p className="text-xs text-muted">
              {t("licenses.expiryAbsoluteHelp", "Expiry is absolute and does not move when Activation occurs.")}
            </p>
          </div>
        </div>
      </fieldset>

      <div className="mt-6 border-t border-line pt-5">
        <LicenseConfigurationFields
          product={result.product}
          kind={result.license.kind}
          baselinePolicy={result.license.policy as unknown as Record<string, unknown>}
          onDraftInvalidChange={setConfigurationInvalid}
          entitlements={entitlements}
          policy={policy}
          disabled={!allowed || saving}
          onEntitlementsChange={setEntitlements}
          onPolicyChange={setPolicy}
        />
      </div>

      <fieldset disabled={!allowed || saving} className="mt-6 min-w-0 space-y-6">
        <ConfigurationChangeSummary beforeEntitlements={JSON.stringify(stripEntitlementKeys(baseline.license.entitlements ?? {}))}
          entitlements={entitlements} beforePolicy={baselinePolicy} policy={policy} />
        <div className="space-y-2 border-t border-line pt-5">
          <Label htmlFor="license-edit-reason">
            {t("dialogs.reason", "Reason")} <span className="text-danger">*</span>
          </Label>
          <Input
            id="license-edit-reason"
            value={reason}
            onChange={(event) => { setReason(event.target.value); setReasonInvalid(false); }}
            placeholder={t("licenses.editReasonPlaceholder", "e.g. seat increase approved by account owner")}
            aria-invalid={reasonInvalid}
            required
          />
        </div>

        {(validation ?? validityMessage) && (
          <InlineAlert
            title={t("licenses.saveErrorTitle", "Could not save License")}
            message={validation ?? validityMessage!}
            variant="error"
          />
        )}
      </fieldset>

      <footer className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-line pt-5">
        <div className="min-w-0 text-xs">
          <p ref={saveStatusRef} role="status" aria-atomic="true" tabIndex={-1}
            className="flex flex-wrap items-center gap-x-2 gap-y-1 text-muted focus:outline-none">
            {saving ? t("common.saving", "Saving…") : dirty || hasHandoffChange
              ? t("licenses.configurationUnsaved", "Unsaved changes")
              : savedAt ? <>
                <Check className="size-4 shrink-0 text-success" aria-hidden="true" />
                <span className="text-ink">{t("licenses.configurationSaved", "Saved")}</span>
                <time dateTime={savedAt}>{formatDateTime(savedAt)}</time>
              </> : null}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {savedAt && !dirty && !saving && overviewHref && (
            <Link to={overviewHref} state={overviewState}
              className="inline-flex min-h-9 items-center px-3 text-sm text-focus underline-offset-4 hover:underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-focus">
              {t("licenses.viewOverview", "View Overview")}
            </Link>
          )}
          <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
            {t("common.cancel", "Cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={!allowed || saving || (!dirty && !hasHandoffChange)}>
            {saving ? t("common.saving", "Saving…") : t("licenses.submitSave", "Save changes")}
          </Button>
        </div>
      </footer>
		</form>
		<SeatLimitReductionDialog
			open={reductionOpen}
			onOpenChange={setReductionOpen}
			activeKeys={activeKeys}
			activeKeyCount={productKeysQuery.data?.pages[0]?.active_key_count ?? result.active_activation_count}
			targetLimit={reductionTarget}
			selectedKeyIDs={selectedKeyIDs}
			onSelectedKeyIDsChange={setSelectedKeyIDs}
			loading={productKeysQuery.isPending || productKeysQuery.hasNextPage}
			busy={reduceMutation.isPending}
			onConfirm={() => reductionTarget !== null && reduceMutation.mutate({ maxDevices: reductionTarget, productKeyIDs: selectedKeyIDs })}
	/>
	</>
  );
}

function SeatLimitReductionDialog({
  open, onOpenChange, activeKeys, activeKeyCount, targetLimit, selectedKeyIDs,
  onSelectedKeyIDsChange, loading, busy, onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeKeys: ProductKeyMetadata[];
  activeKeyCount: number;
  targetLimit: number | null;
  selectedKeyIDs: string[];
  onSelectedKeyIDsChange: (ids: string[]) => void;
  loading: boolean;
  busy: boolean;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const required = targetLimit === null ? 0 : Math.max(0, activeKeyCount - targetLimit);
  const complete = required > 0 && selectedKeyIDs.length === required && activeKeys.length >= activeKeyCount;
  return <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent showClose={!busy}>
      <DialogHeader>
        <DialogTitle>{t("licenses.reduceSeatLimitTitle", "Choose seats to revoke")}</DialogTitle>
        <DialogDescription>{t("licenses.reduceSeatLimitDescription", "Select exactly {{count}} Product Key(s). Their linked active Device, if any, will be deactivated.", { count: required })}</DialogDescription>
      </DialogHeader>
      <p className="mt-3 text-xs leading-5 text-muted">
        {t(
          "dialogs.deactivateActivationOfflineConsequence",
          "This is not an immediate remote kill. A signed offline token may continue to work until its bounded expiry or offline window ends.",
        )}
      </p>
      <div className="mt-4 max-h-72 space-y-2 overflow-y-auto">
		{loading ? <p className="text-sm text-muted">{t("common.loading", "Loading…")}</p> : activeKeys.map((key) => {
		  const checked = selectedKeyIDs.includes(key.id);
		  const disabled = !checked && selectedKeyIDs.length >= required;
		  const device = key.current_activation;
		  return <label className="flex cursor-pointer items-center gap-3 rounded-md border border-line p-3 text-sm" key={key.id}>
			<Checkbox checked={checked} disabled={disabled || busy} onCheckedChange={(next) => onSelectedKeyIDsChange(next ? [...selectedKeyIDs, key.id] : selectedKeyIDs.filter((id) => id !== key.id))} />
			<span><span className="block font-mono text-xs">{key.masked_prefix ? `${key.masked_prefix}-` : ""}••••-{key.masked_suffix}</span><span className="block text-xs text-muted">{device ? t("licenses.reduceSeatLimitDeviceImpact", "In use: {{device}} will be deactivated", { device: device.computer_name || device.installation_id }) : t("licenses.reduceSeatLimitAvailable", "Available: no active Device")}</span></span>
			<span className="ml-auto font-mono text-[11px] text-muted">{key.id}</span>
		  </label>;
        })}
      </div>
      <p className="mt-3 text-xs text-muted">{selectedKeyIDs.length} / {required} {t("licenses.seatsSelected", "seats selected")}</p>
      <DialogFooter>
        <Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>{t("common.cancel", "Cancel")}</Button>
        <Button variant="danger" disabled={!complete || busy} onClick={onConfirm}>{busy ? <BusyLabel>{t("common.saving", "Saving…")}</BusyLabel> : t("licenses.confirmReduceSeatLimit", "Revoke selected seats")}</Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>;
}

function policyJson(result: SearchResult, suggestedMaxDevices?: number) {
  const currentLimit = result.license.policy.max_devices;
  return JSON.stringify({
    ...result.license.policy,
    max_devices: suggestedMaxDevices && suggestedMaxDevices > currentLimit
      ? suggestedMaxDevices
      : currentLimit,
  }, null, 2);
}

function stripEntitlementKeys(
  entitlements: SearchResult["license"]["entitlements"],
): Record<string, { type: string; value: unknown }> {
  return Object.fromEntries(
    Object.entries(entitlements ?? {}).map(([key, entitlement]) => [
      key,
      { type: entitlement.type, value: entitlement.value },
    ]),
  );
}
